import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Zap, Play, Pause, Trash2, ArrowRight, Clock, AlertTriangle } from 'lucide-react';
import { RequireCapability } from '../../../components/auth/RequireCapability';
import { AdminConsoleLayout } from '../components/AdminConsoleLayout';
import { ModulePageHeader, ModuleLoadingState, ModuleCard } from '../../shared/components/ModulePageHeader';
import { EmptyState } from '../../shared/components/EmptyState';
import { ErrorState } from '../../shared/components/ErrorState';
import { EnterpriseStatusBadge } from '../../shared/components/EnterpriseStatusBadge';
import { useWorkspace } from '../../../contexts/WorkspaceContext';
import { supabase } from '../../../lib/supabaseClient';
import {
  getAutomations,
  createAutomation,
  toggleAutomation,
  deleteAutomation,
} from '../../../services/platformService';
import type { Automation, AutomationTrigger, AutomationAction } from '../../../types/platformTypes';

const TRIGGER_LABELS: Record<AutomationTrigger, string> = {
  workflow_completed: 'Workflow Concluído',
  action_due: 'Ação com Prazo',
  action_completed: 'Ação Concluída',
  user_invited: 'Usuário Convidado',
  invoice_due: 'Fatura Vencendo',
  event_bus: 'Evento Customizado',
  schedule: 'Agendado',
  webhook_received: 'Webhook Recebido',
};

const ACTION_LABELS: Record<AutomationAction, string> = {
  create_task: 'Criar Tarefa',
  send_email: 'Enviar Email',
  send_slack: 'Enviar Slack',
  trigger_webhook: 'Disparar Webhook',
  update_status: 'Atualizar Status',
  create_notification: 'Criar Notificação',
  log_event: 'Registrar Evento',
  run_script: 'Executar Script',
};

const AutomationCard: React.FC<{
  automation: Automation;
  onToggle: (id: string, enabled: boolean) => void;
  onDelete: (id: string) => void;
}> = ({ automation, onToggle, onDelete }) => (
  <div className="border border-[var(--border-default)] rounded p-4 bg-[var(--bg-surface)]">
    <div className="flex items-start justify-between mb-3">
      <div className="flex-1 min-w-0">
        <h3 className="text-sm font-semibold text-[var(--text-primary)] truncate">{automation.name}</h3>
        {automation.description && (
          <p className="text-[11px] text-[var(--text-tertiary)] mt-0.5 truncate">{automation.description}</p>
        )}
      </div>
      <div className="flex items-center gap-1 ml-2">
        <button
          onClick={() => onToggle(automation.id, !automation.enabled)}
          className="p-1 rounded hover:bg-[var(--bg-elevated)] transition-colors"
          title={automation.enabled ? 'Pausar' : 'Ativar'}
        >
          {automation.enabled ? (
            <Pause size={14} className="text-amber-500" />
          ) : (
            <Play size={14} className="text-emerald-500" />
          )}
        </button>
        <button
          onClick={() => onDelete(automation.id)}
          className="p-1 rounded hover:bg-[var(--bg-elevated)] transition-colors"
          title="Excluir"
        >
          <Trash2 size={14} className="text-[var(--text-tertiary)] hover:text-red-500" />
        </button>
      </div>
    </div>

    {/* Trigger → Action flow */}
    <div className="flex items-center gap-2 mb-3">
      <span className="inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded bg-blue-500/10 text-blue-600 dark:text-blue-400">
        <Zap size={10} />
        {TRIGGER_LABELS[automation.trigger_type] || automation.trigger_type}
      </span>
      <ArrowRight size={12} className="text-[var(--text-tertiary)] shrink-0" />
      <span className="inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
        {ACTION_LABELS[automation.action_type] || automation.action_type}
      </span>
    </div>

    <div className="flex items-center justify-between text-[11px] text-[var(--text-tertiary)]">
      <div className="flex items-center gap-3">
        <span className="flex items-center gap-1">
          <Play size={10} />
          {automation.execution_count} execuções
        </span>
        {automation.last_executed && (
          <span className="flex items-center gap-1">
            <Clock size={10} />
            {new Date(automation.last_executed).toLocaleDateString('pt-BR')}
          </span>
        )}
      </div>
      <EnterpriseStatusBadge
        status={automation.enabled ? 'active' : 'suspended'}
        label={automation.enabled ? 'Ativo' : 'Pausado'}
      />
    </div>

    {automation.last_error && (
      <div className="mt-2 flex items-center gap-1 text-[11px] text-red-500">
        <AlertTriangle size={10} />
        <span className="truncate">{automation.last_error}</span>
      </div>
    )}
  </div>
);

interface AdminAutomationsPageProps {
  externalShowCreate?: boolean;
  onCloseCreate?: () => void;
}

export const AdminAutomationsPage: React.FC<AdminAutomationsPageProps> = ({ externalShowCreate, onCloseCreate }) => {
  const qc = useQueryClient();
  const { workspace } = useWorkspace();
  const orgId = workspace?.organizationId ?? '';
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newTrigger, setNewTrigger] = useState<AutomationTrigger>('workflow_completed');
  const [newAction, setNewAction] = useState<AutomationAction>('create_notification');

  // Sync external create trigger
  React.useEffect(() => {
    if (externalShowCreate) setShowCreate(true);
  }, [externalShowCreate]);

  const handleCloseCreate = () => {
    setShowCreate(false);
    onCloseCreate?.();
  };

  // Resolve workspace_id — use orgId directly as fallback
  const workspaceId = orgId;

  const automationsQuery = useQuery({
    queryKey: ['admin-automations', workspaceId],
    queryFn: () => getAutomations(workspaceId || undefined),
  });

  const toggleMut = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) => toggleAutomation(id, enabled),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-automations'] }),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteAutomation(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-automations'] }),
  });

  const createMut = useMutation({
    mutationFn: () =>
      createAutomation({
        workspace_id: workspaceId,
        name: newName,
        trigger_type: newTrigger,
        action_type: newAction,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-automations'] });
      handleCloseCreate();
      setNewName('');
    },
  });

  const automations = automationsQuery.data || [];
  const activeCount = automations.filter((a) => a.enabled).length;
  const totalExecutions = automations.reduce((sum, a) => sum + a.execution_count, 0);

  return (
    <RequireCapability capability="admin.access" realRoleOnly>
      <AdminConsoleLayout>
        <div className="enterprise-page h-full overflow-y-auto">
          <ModulePageHeader icon={Zap} title="Automações" subtitle="Motor de automação: trigger → condição → ação"
            rightContent={
              <span className="text-[11px] px-2.5 py-1 rounded-sm border border-amber-300 bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800 font-medium">
                Em breve
              </span>
            }
          />

          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 px-6 mb-6">
            <ModuleCard>
              <div className="text-center">
                <p className="text-2xl font-bold text-[var(--text-primary)]">{automations.length}</p>
                <p className="text-xs text-[var(--text-secondary)]">Automações</p>
              </div>
            </ModuleCard>
            <ModuleCard>
              <div className="text-center">
                <p className="text-2xl font-bold text-emerald-500">{activeCount}</p>
                <p className="text-xs text-[var(--text-secondary)]">Ativas</p>
              </div>
            </ModuleCard>
            <ModuleCard>
              <div className="text-center">
                <p className="text-2xl font-bold text-[var(--text-primary)]">{totalExecutions}</p>
                <p className="text-xs text-[var(--text-secondary)]">Execuções Total</p>
              </div>
            </ModuleCard>
            <ModuleCard>
              <div className="text-center">
                <p className="text-2xl font-bold text-amber-500">
                  {automations.filter((a) => a.last_error).length}
                </p>
                <p className="text-xs text-[var(--text-secondary)]">Com Erros</p>
              </div>
            </ModuleCard>
          </div>

          {/* Create Button */}
          <div className="px-6 mb-4 flex justify-between items-center">
            <h2 className="text-sm font-semibold text-[var(--text-primary)]">Regras de Automação</h2>
          </div>

          {/* Create Form */}
          {showCreate && (
            <div className="px-6 mb-4">
              <ModuleCard>
                <div className="space-y-3 p-2">
                  <input
                    type="text"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    placeholder="Nome da automação"
                    className="w-full px-3 py-2 text-sm rounded border border-[var(--border-default)] bg-[var(--bg-surface)] text-[var(--text-primary)] focus:outline-none focus:border-[var(--enterprise-primary,#2563eb)]"
                  />
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[11px] text-[var(--text-tertiary)] mb-1">Trigger</label>
                      <select
                        value={newTrigger}
                        onChange={(e) => setNewTrigger(e.target.value as AutomationTrigger)}
                        className="w-full px-2 py-1.5 text-xs rounded border border-[var(--border-default)] bg-[var(--bg-surface)] text-[var(--text-primary)]"
                      >
                        {Object.entries(TRIGGER_LABELS).map(([k, v]) => (
                          <option key={k} value={k}>{v}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-[11px] text-[var(--text-tertiary)] mb-1">Ação</label>
                      <select
                        value={newAction}
                        onChange={(e) => setNewAction(e.target.value as AutomationAction)}
                        className="w-full px-2 py-1.5 text-xs rounded border border-[var(--border-default)] bg-[var(--bg-surface)] text-[var(--text-primary)]"
                      >
                        {Object.entries(ACTION_LABELS).map(([k, v]) => (
                          <option key={k} value={k}>{v}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="flex gap-2 justify-end">
                    <button
                      onClick={() => handleCloseCreate()}
                      className="px-3 py-1.5 text-xs rounded border border-[var(--border-default)] text-[var(--text-secondary)] hover:bg-[var(--bg-elevated)]"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={() => createMut.mutate()}
                      disabled={!newName.trim() || !workspaceId || createMut.isPending}
                      className="px-3 py-1.5 text-xs rounded bg-[var(--enterprise-primary,#2563eb)] text-white hover:opacity-90 disabled:opacity-50"
                    >
                      {createMut.isPending ? 'Criando...' : 'Criar'}
                    </button>
                  </div>
                </div>
              </ModuleCard>
            </div>
          )}

          {/* Automations List */}
          <div className="px-6 pb-8">
            {automationsQuery.isPending && <ModuleLoadingState label="Carregando automações" />}

            {automationsQuery.isError && (
              <ErrorState message="Erro ao carregar automações." onRetry={automationsQuery.refetch} />
            )}

            {!automationsQuery.isPending && !automationsQuery.isError && automations.length === 0 && (
              <EmptyState
                icon={Zap}
                title="Nenhuma automação configurada"
                description="Crie regras de automação para conectar triggers a ações automaticamente."
              />
            )}

            {automations.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {automations.map((a) => (
                  <AutomationCard
                    key={a.id}
                    automation={a}
                    onToggle={(id, enabled) => toggleMut.mutate({ id, enabled })}
                    onDelete={(id) => deleteMut.mutate(id)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </AdminConsoleLayout>
    </RequireCapability>
  );
};
